// SignInPage.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSpring, animated } from "react-spring";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faCheckCircle,
  faUser,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../assets/logo.jpg";
import { useUser } from "ogcommon";          // project-specific user context

function SignInPage() {
  const navigate = useNavigate();
  const { setUser } = useUser();            // context setter

  /* ------------------------- STATE ------------------------- */
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [subdomain, setSubdomain] = useState(""); // retained for future use

  /* -------------------- QUERY-STRING PRE-FILL -------------------- */
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);

    /* uid → username  (case-insensitive fallbacks) */
    const uid =
      qs.get("uid") ||
      qs.get("UID") ||
      qs.get("user") ||
      qs.get("username");
    if (uid) setUsername(decodeURIComponent(uid));

    /* subdomain  (optional) */
    const sub =
      qs.get("subdomain") ||
      qs.get("Subdomain");
    if (sub) setSubdomain(decodeURIComponent(sub));
  }, []);

  /* ------------------------- ANIMATION ------------------------ */
  const formAnimation = useSpring({
    opacity: 1,
    transform: "translateY(0)",
    from: { opacity: 0, transform: "translateY(50px)" },
    config: { mass: 1, tension: 200, friction: 20 },
  });

  /* --------------------- UTILITIES --------------------- */
  function hashPassword(pw) {
    let hash = 0;
    for (let i = 0; i < pw.length; i++) {
      hash = (hash << 5) - hash + pw.charCodeAt(i);
      hash |= 0;                              // 32-bit
    }
    return hash.toString();
  }

  /* ---------------- PASSWORD RESET ---------------- */
  async function handleForgotPassword() {
    if (username.trim() === "") {
      setError("Please enter a username");
      return;
    }
    try {
      const baseUrl = "https://house.ogendeavors.com";
      const res = await fetch(`${baseUrl}/api/passwordreset.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userID: username }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMessage("Password-reset email sent");
        setError("");
      } else {
        setError("Failed to send password-reset email");
        setSuccessMessage("");
      }
    } catch {
      setError("An error occurred while sending the reset email.");
      setSuccessMessage("");
    }
  }

  /* ------------------------ SIGN-IN ------------------------ */
  async function handleSignIn(e) {
    e.preventDefault();

    /* Offline mode */
    if (!navigator.onLine) {
      return handleOfflineSignIn();
    }

    setLoading(true);
    try {
      const baseUrl = "https://house.ogendeavors.com";
      const res = await fetch(`${baseUrl}/api/login_api.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const { success, message, user, frontend_access } = await res.json();
      if (!success) {
        setError(message);
        return;
      }

      /* Store scoped admin access token in sessionStorage for DatabaseManager.
         The token is short-lived (10 min) and audience-scoped to ogsupport-db-manager.
         sessionStorage is cleared when the browser tab closes, which is appropriate
         for a short-lived privileged credential. */
      if (frontend_access?.access_token) {
        sessionStorage.setItem("og_access_token", frontend_access.access_token);
      } else {
        sessionStorage.removeItem("og_access_token");
      }

      /* response unpack */
      const {
        UserID,
        Role,
        GroupID,
        FullName,
        Phone,
        CompanyName,
        Subdomain,
        Company,
      } = user;
      const companyName = CompanyName ?? Company ?? "";

      /* context write */
      setUser({
        id: UserID,
        role: Role,
        company: companyName,
        jRole: GroupID ?? "",
        sub: Subdomain,
        email: "",
        phone: Phone ?? "",
      });

      /* local persistence */
      localStorage.setItem("admin_user", JSON.stringify(user));
      localStorage.setItem(
        "credentials",
        JSON.stringify({
          username,
          password: hashPassword(password),
          companyName,
        })
      );

      /* nav */
      if (Role === "C" || Subdomain) {
        navigate(`/companyHome/${encodeURIComponent(Subdomain)}`);
      } else {
        navigate("/home");
      }
    } catch {
      setError("An error occurred while signing in.");
    } finally {
      setLoading(false);
    }
  }

  /* ------------------- OFFLINE FALLBACK ------------------ */
  function handleOfflineSignIn() {
    const stored = JSON.parse(localStorage.getItem("credentials"));
    if (!stored) {
      setError("No saved credentials. Sign in online first.");
      return;
    }
    const enteredHash = hashPassword(password);
    if (username === stored.username && enteredHash === stored.password) {
      const adminUser = JSON.parse(localStorage.getItem("admin_user") || "{}");
      setUser(adminUser);
      navigate("/home");
    } else {
      setError("Invalid credentials. Sign in online first.");
    }
  }

  /* ---------------------------- UI ---------------------------- */
  return (
    <div className="min-h-screen flex flex-col justify-center items-center relative overflow-hidden bg-gradient-to-r from-gray-100 to-gray-200">
      <animated.div
        style={formAnimation}
        className="bg-white shadow-2xl rounded-lg px-6 py-10 sm:px-12 sm:py-14 max-w-md w-full transform hover:scale-105 transition-transform duration-500 relative z-10 flex flex-col justify-center min-h-screen sm:min-h-0"
      >
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-gray-400 to-gray-500 rounded-t-lg animate-pulse" />
        <img
          src={logo}
          className="w-40 sm:w-32 mx-auto mb-6 sm:mb-8 rounded-full border-4 border-white shadow-md animate-float"
          alt="logo"
        />

        <p className="text-xl font-semibold text-gray-800 text-center mb-6">
          admin
        </p>

        <form className="space-y-6 sm:space-y-8" onSubmit={handleSignIn}>
          {/* USERNAME */}
          <div>
            <label
              htmlFor="username"
              className="block text-gray-700 font-semibold mb-2 text-base sm:text-lg"
            >
              <FontAwesomeIcon icon={faUser} className="mr-2" />
              Username
            </label>
            <input
              id="username"
              type="text"
              placeholder="Enter your username"
              className="w-full border-gray-300 border-2 rounded-md px-4 py-2 sm:px-5 sm:py-3 text-base sm:text-lg focus:outline-none focus:ring-4 focus:ring-gray-400 focus:border-transparent transition-all duration-300"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          {/* PASSWORD */}
          <div>
            <label
              htmlFor="password"
              className="block text-gray-700 font-semibold mb-2 text-base sm:text-lg"
            >
              <FontAwesomeIcon icon={faLock} className="mr-2" />
              Password
            </label>
            <input
              id="password"
              type="password"
              placeholder="Enter your password"
              className="w-full border-gray-300 border-2 rounded-md px-4 py-2 sm:px-5 sm:py-3 text-base sm:text-lg focus:outline-none focus:ring-4 focus:ring-gray-400 focus:border-transparent transition-all duration-300"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {/* FEEDBACK */}
          {successMessage && (
            <p className="text-green-500 text-base sm:text-lg animate-pulse">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
              {successMessage}
            </p>
          )}
          {error && (
            <p className="text-red-500 text-base sm:text-lg animate-pulse">
              <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
              {error}
            </p>
          )}

          {/* SUBMIT */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-full hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-gray-600 transform hover:scale-105 transition-transform duration-500 shadow-lg text-lg sm:text-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? "Signing In…" : "Sign In"}
          </button>

          {/* FORGOT PASSWORD */}
          <p className="text-gray-600 text-center text-base sm:text-lg">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-gray-700 hover:text-gray-800 underline transition-colors duration-300"
            >
              Forgot Password?
            </button>
          </p>
        </form>
      </animated.div>
    </div>
  );
}

export default SignInPage;
